<script>
    import { onMount } from "svelte";
    import DateInput from "../../../Components/Common/DateInput.svelte";
    import NotificationModal from "../../../Components/Common/NotificationModal.svelte";
    import { apiMethods } from "../../../Services/index.svelte";
    import { getFormatDate, getInsuranceType, getValidBillingData, goToURL, isObjectEmpty, showError, showInfo } from "../../../Utils/utilFunctions.svelte";
    import { _getSurgeonFullName, openViewEncounterSidePanel } from "../helpers/encounterHelper.svelte";
    import { addErrorInput, removeErrorInput } from "../helpers/encounterUtils.svelte";
    import CustomTooltip from "../../../Components/Common/CustomTooltip.svelte";
    import { NoteTypes } from "../../../Utils/utilConstants.svelte";
    import ConfirmActionModal from "../../../Components/Common/ConfirmActionModal.svelte";

    export let row;
    export let isOpen;
    export let onClose;
    export let saveTableScroll;
    export let isFromCalendarList;

    let isConvertingQuoteToEncounter = false,
        availableEncountersForConverstion = [],
        selectedEncounter = null,
        selectedFeeEstimate = null,
        loading = false,
        errInputs = [];

    let isConfirmActionModalOpen = false;

    onMount(async () => {
        loadData();
	});

    const loadData = async () => {
        try {

            loading = true;

            //get fee estimate full details
            var feeEstimate = await apiMethods.getMethod.getPatientEncounterById(row.id);
            if (!feeEstimate || feeEstimate.error) {
                showError(feeEstimate.error ?? "Failed to locate fee estimate details, please refresh the page and try again and contact support if the issue persists.",);
                loading = false;
                return;
            } else {
                //set selected fee estimate details
                selectedFeeEstimate = feeEstimate;
            }

            //at this moment, fee estimate details loaded completely.
            //match patient details
            var matchedPatient = await apiMethods.postMethod.matchPatient(feeEstimate.patient, feeEstimate.doctor?.id);
            if(!matchedPatient || matchedPatient.error) {
                console.log(matchedPatient.error ?? "Failed to match fee estimate patient details, please refresh the page and try again and contact support if the issue persists.")
                loading = false;
                return;
            }

            //patient successfully matched, get future patient encounters
            var futureEncountersResponse = await apiMethods.getMethod.getPatientEncounter(
                    0,
                    999999,
                    {
                        "filter[patientId]": matchedPatient.id,
                        "filter[scheduleddatefrom]": new Date().toISOString().split('T')[0], //starts with today
                        "filter[billingstatus]": 0,
                        "filter[quoteonly]": 0
                    },
                );
            if(!futureEncountersResponse || futureEncountersResponse.error) {
                console.log(futureEncountersResponse.error ?? "Failed to load existing encounters for this patient, please refresh the page and try again and contact support if the issue persists.")
                loading = false;
                return;
            }
            if(futureEncountersResponse.results.length > 0){
                availableEncountersForConverstion = filterMatchedEncountersBasedOnQuote(feeEstimate, futureEncountersResponse.results);
            }
            loading = false;

        }
        catch(err) {
            showError(err, undefined, true);
            loading = false;
        }

    }

    const validateScheduledDate = () => {
        if (row.scheduledDate || row.tempScheduledDate) {
            errInputs = removeErrorInput(
                "scheduledDateField",
                errInputs,
                "Scheduled date",
            );
            return true;
        }

        errInputs = addErrorInput(
            "scheduledDateField",
            errInputs,
            "Scheduled date",
        );

        return false;
    };

    const adminNoteDetails = (feeEstimateData) => {
        const isAsaCodes = feeEstimateData.billingCodesType == 1;
        return `This patient was sent / accepted a fee estimate with item numbers ${isAsaCodes ? feeEstimateData.asaData.map((e) => e.itemCode).join(", ") : feeEstimateData.mbsData.map((e) => e.itemNum).join(",")} for \$${feeEstimateData.patientPaysTotalFee ? feeEstimateData.totalFee : feeEstimateData.actualGap} as a ${feeEstimateData.patientPaysTotalFee ? "TOTAL" : "GAP"}`;
    }

    const convertQuoteToEncounter = async () => {

        isConvertingQuoteToEncounter = true;
        try {

            if(selectedEncounter == null) {
                if (!validateScheduledDate() && !selectedEncounter) {
                    showInfo("Scheduled date is mandatory when converting fee estimates to encounters.");
                    return;
                } 
                //create new encounter
                //sync estimate details into new encounter
                let encounterDetails = _syncEncounterDetails(selectedFeeEstimate);

                if (isObjectEmpty(encounterDetails)) {
                    isConvertingQuoteToEncounter = false;
                    onClose();
                    return;
                }

                //set selected schedule date and time
                if (!row.tempScheduledTime) row.tempScheduledTime = "00:00:00";
                else if (row.tempScheduledTime.split(":").length < 3)
                    row.tempScheduledTime += ":00";

                if (row.tempScheduledDate)
                    encounterDetails.scheduledDate =
                        row.tempScheduledDate.split("T")[0] +
                        "T" +
                        row.tempScheduledTime;

                //create encounter from fee estimate
                var newEncounter = await apiMethods.postMethod.createPatientEncounter(encounterDetails);
                if (!newEncounter || newEncounter.error) {
                    //failed on creating encounter
                    showError(
                        newEncounter.error ??
                            "Failed to convert selected fee estimate to an encounter, please refresh the page and try again and contact support if the issue persists.",
                    );
                }
                else {
                    //encounter created successfully
                    //load fee estimate notes
                    var estimateNotes = await apiMethods.getMethod.getNotes(2, selectedFeeEstimate.id);
                    if (!estimateNotes || estimateNotes.error) {
                        console.log("Failed getting notes of fee estimate")
                    } else {
                        var feeEstimateNotes = [...estimateNotes.results];
                        if(feeEstimateNotes.length > 0) {
                            for(let i = 0; i< feeEstimateNotes.length; i++){
                                //recreate note item
                                const selectedNote = {
                                    ...feeEstimateNotes[i],
                                    relId: newEncounter.id, //encounter id
                                    notifyDoctor: false,
                                    id: 0
                                };

                                await apiMethods.postMethod.createNote(selectedNote).then((res) => {
                                    if (!res || res.error)
                                        console.log("Failed to create note");
                                });
                            }
                        }
                    }

                    //update fee estimte to canceled
                    var updateFeeEstimateResponse = await apiMethods.putMethod.updatePatientEncounter({
                                    ...selectedFeeEstimate,
                                    billingStatus: Number(5),
                                    quoteEncounterId: Number(newEncounter.id),
                                }, false, "Fee estimate to Cancelled");
                    if (!updateFeeEstimateResponse || updateFeeEstimateResponse.error) {
                        showError(
                            updateFeeEstimateResponse.error ??
                                "Failed to update selected fee estimate billing status, please refresh the page and try again and contact support if the issue persists.",
                        );
                    }

                    if(saveTableScroll){
                        saveTableScroll();
                    }

                    if (isFromCalendarList)
                        openViewEncounterSidePanel({
                            encounterId: newEncounter.id,
                            onCloseFunction: () => afterModifyNotes(),
                        });
                    else goToURL(`/PatientEncounter/view/${newEncounter.id}`);
                }
                  
            } else {
                //match on selected encounter
                let encounterDetails = { ...selectedEncounter };
                
                //update encounter insurance type if quote ins type is cosmetic or overseas and diff than matched enc ins type
                var quoteInsuranceType = getInsType(selectedFeeEstimate);
                var encounterInsuranceType = getInsType(encounterDetails);
                if([10, 15].includes(quoteInsuranceType) && quoteInsuranceType != encounterInsuranceType){
                    encounterDetails.billingData = updateExistingBillingDataInsuranceDetails(encounterDetails.billingData, selectedFeeEstimate.patient);
                }
                //copy item numbers and billing rule to matched enc
                var feeEstimateQuoteData = selectedFeeEstimate.billingData.find((bd) => bd.isLatestQuoteData);
                var encounterQuoteData = encounterDetails.billingData.find((bd) => bd.isLatestQuoteData);
                if(!encounterQuoteData) {
                    //initialize new billing data object, practically create an exact copy of the quote billing data
                    //this will include all the totals, so no need for calculation
                    encounterDetails.billingData = [
                        {
                            ...feeEstimateQuoteData, 
                            id: 0,
                            patientEncounterId: encounterDetails.id, //link to encounter
                        }
                    ];
                } else {
                    //set old encounter quote data as not latest billing data
                    encounterQuoteData.isLatestQuoteData = false;
                    encounterQuoteData.isLatestBillingData = false;
                    //add new billing data to the encounter, and set it as latest quote one.
                    encounterDetails.billingData = [
                        ...encounterDetails.billingData,
                        {
                            ...feeEstimateQuoteData, 
                            id: 0,
                            patientEncounterId: encounterDetails.id, //link to encounter
                            isLatestQuoteData: true
                        }
                    ]
                }

                //update encounter to match quote details
                //update quote and link it to the encounter
                var updateEncounterResponse = await apiMethods.putMethod.updatePatientEncounter({
                    ...encounterDetails,
                    quoteEncounterId: Number(selectedFeeEstimate.id),
                }, false, "Update encounter from fee estimate");
                if (!updateEncounterResponse || updateEncounterResponse.error) {
                    showError(
                        updateEncounterResponse.error ??
                            "Failed to update selected fee estimate billing status, please refresh the page and try again and contact support if the issue persists.",
                    );
                    return;
                }
                //copy existing fee estimate notes
                var estimateNotes = await apiMethods.getMethod.getNotes(2, selectedFeeEstimate.id);
                if (!estimateNotes || estimateNotes.error) {
                    console.log("Failed getting notes of fee estimate")
                } else {
                    var feeEstimateNotes = [...estimateNotes.results];
                    if(feeEstimateNotes.length > 0) {
                        for(let i = 0; i< feeEstimateNotes.length; i++){
                            //recreate note item
                            const selectedNote = {
                                ...feeEstimateNotes[i],
                                relId: encounterDetails.id, //encounter id
                                notifyDoctor: false,
                                id: 0
                            };

                            await apiMethods.postMethod.createNote(selectedNote).then((res) => {
                                if (!res || res.error)
                                    console.log("Failed to create note");
                            });
                        }
                    }
                }

                //create admin note on encounter
                const adminNote = {
                    relTo: NoteTypes.PatientEncounter,
                    relId: encounterDetails.id, //encounter id
                    notifyDoctor: false,
                    visibleToDoctors: true,
                    content: adminNoteDetails(feeEstimateQuoteData)
                };

                await apiMethods.postMethod.createNote(adminNote).then((res) => {
                    if (!res || res.error)
                        console.log("Failed to create admin note on encounter");
                });

                //update quote and link it to the encounter
                var updateFeeEstimateResponse = await apiMethods.putMethod.updatePatientEncounter({
                    ...selectedFeeEstimate,
                    billingStatus: Number(5),
                    quoteEncounterId: Number(selectedEncounter.id),
                }, false, "Fee estimate to Cancelled");
                if (!updateFeeEstimateResponse || updateFeeEstimateResponse.error) {
                    showError(
                        updateFeeEstimateResponse.error ??
                            "Failed to update selected fee estimate billing status, please refresh the page and try again and contact support if the issue persists.",
                    );
                    return;
                }

                if(saveTableScroll){
                    saveTableScroll();
                }
                //open encounter details screen
                if (isFromCalendarList)
                    openViewEncounterSidePanel({
                        encounterId: encounterDetails.id,
                        onCloseFunction: () => afterModifyNotes(),
                    });
                else goToURL(`/PatientEncounter/view/${encounterDetails.id}`);

            }

            isConvertingQuoteToEncounter = false;

            onClose();
        }
        catch(err){
            showError(err, undefined, true);
        }
    };

    const updateExistingBillingDataInsuranceDetails = (billingData, patient) => {
        if(billingData.length > 0) {
            for(let i=0; i < billingData.length; i++) {
                billingData[i].patientInsuranceType = patient.insuranceType;
                billingData[i].patientMedicareNo = patient.medicareNo;
                billingData[i].patientMedicareRefNo =
                    patient.medicareRefNo != null ? patient.medicareRefNo : null;
                billingData[i].patientInsurance = patient.insurance;
                billingData[i].patientVeteranNo = patient.veteranNo;
                billingData[i].patientInsuranceId = patient.insuranceId;
                billingData[i].patientMembershipNo = patient.membershipNo;
                billingData[i].patientInsuranceState = patient.insuranceState;
                billingData[i].patientMembershipRefNo =
                patient.membershipRefNo != null ? patient.membershipRefNo : null;
            }
        }
        return billingData;
    }

    const _syncEncounterDetails = (res) => ({
        ...res,
        billingStatus: Number(0),
        quoteOnly: Boolean(false),
        quoteEncounterId: Number(res.id),
    });

    const filterMatchedEncountersBasedOnQuote = (feeEstimate, encounters) => {
        let matchedEncounters = [];
        let quoteInsuranceType = getInsType(feeEstimate);

        if(quoteInsuranceType == 0) {
            //check for any private encounters
            let privateEncounters = encounters.filter((e) => getInsType(e) == quoteInsuranceType);
            let quoteInsuranceId = getInsId(feeEstimate);
            if(quoteInsuranceId && quoteInsuranceId > 0) {
                //fee estimate fund id not null, get all private encounters with the same fund
                var privateEncountersSameFund = privateEncounters.filter((e) => getInsId(e) == quoteInsuranceId);
                if(privateEncountersSameFund.length > 0) {
                    matchedEncounters = [...privateEncountersSameFund];
                }
            }
            else {
                matchedEncounters = [...privateEncounters];
            }
        }
        else if(quoteInsuranceType == 11) {
            let sameInsuranceTypeEncounters = encounters.filter((e) => getInsType(e) == quoteInsuranceType);
            if(sameInsuranceTypeEncounters.length > 0)
                matchedEncounters = [...sameInsuranceTypeEncounters];
            else matchedEncounters = [...encounters];
        }
        else if(quoteInsuranceType == 10 || quoteInsuranceType == 15) {
            //match encounters on insurance type
            let sameInsuranceTypeEncounters = encounters.filter((e) => getInsType(e) == quoteInsuranceType);
            if(sameInsuranceTypeEncounters.length > 0)
                matchedEncounters = [...sameInsuranceTypeEncounters];
            else matchedEncounters = [...encounters];
        }

        return matchedEncounters;

    }

    const onMatchEncounterSelect = (item) => {
        if(selectedEncounter != null) {
            if(selectedEncounter.id == item.id) {
                //deselect
                selectedEncounter = null;
                return;
            }
        }
        
        selectedEncounter = item;
    }

    const getInsType = (encounter) => {
        return isObjectEmpty(
                getValidBillingData(encounter.billingData),
            )
        ? encounter.patient.insuranceType
        : getValidBillingData(encounter.billingData).insuranceType;
    }

    const getInsId = (encounter) => {
        return isObjectEmpty(
                getValidBillingData(encounter.billingData),
            )
        ? encounter.patient.insuranceId
        : getValidBillingData(encounter.billingData).insuranceId;
    }

</script>

<NotificationModal
        isModalOpen={isOpen}
        title="Convert Selected Fee Estimate to Encounter"
        cancelText="Cancel"
        submitText={isConvertingQuoteToEncounter ? "Converting" : selectedEncounter ? "Match selected" : "Convert to new encounter"}
        isConfirming={isConvertingQuoteToEncounter}
        closeModal={onClose}
        closeModalFunction={onClose}
        confirmAction={() => isConfirmActionModalOpen = true }
        contentClass={"p-6 flex flex-col"}
        modalMaxWidth="max-w-4xl"
    >

    <div class="flex flex-col gap-4">
        <div class="flex flex-col gap-2">
            <div class="text-lg font-bold text-left">
                Convert into new encounter
            </div>
            <div class="flex flex-col gap-4 p-4">
                {#if row.scheduledDate}
                    <p class="text-left">
                        Convert this fee estimate to a new patient encounter?
                    </p>
                {:else}
                    <div class="flex flex-col gap-y-2 justify-start w-full">
                        <p class="text-left">
                            This Fee Estimate does not have a scheduled date, please select a date before converting to a new patient encounter:
                        </p>

                        <div class="flex w-full gap-x-2 items-center">
                            <DateInput
                                placeholder="Scheduled Date"
                                wrapperClass="input input-sm px-0 input-bordered flex-1"
                                wrapperClassFocus="inputFocus"
                                id="scheduledDateField"
                                bind:value={row.tempScheduledDate}
                            />
                            <input
                                type="time"
                                class="input input-sm input-bordered flex-1"
                                bind:value={row.tempScheduledTime}
                            />
                        </div>
                    </div>
                {/if}
            </div>
        </div>
        <div class="divider">or</div>
        <div class="flex flex-col gap-2">
            <div class="text-lg font-bold text-left">
                Convert into existing encounter
                <CustomTooltip tooltipPosition="top">
                    <div class="cursor-pointer" slot="tooltipTrigger">
                        <i
                            class="fa-solid fa-circle-info"
                        >
                        </i>
                    </div>
                
                    <p>We search for future encounters, by scheduled date, with the same patient details. We show only encounters in status Pre-Quote and we filter the results based on the estimate billing details (insurance type, insurance id, etc.)</p>
                </CustomTooltip>
            </div>
            {#if loading}
                <div class="flex flex-col relative items-center">
                    <div class="flex flex-1 justify-between">
                        <h3 class="text-sm">Loading encounters...</h3>
                    </div>
                    <div class="flex mt-6 flex-1 justify-center">
                        <progress class="progress progress-primary w-56" />
                    </div>
                </div>
            {:else}
                <div class="flex flex-col items-left gap-2 p-4">
                    {#if availableEncountersForConverstion.length > 0}
                        <div class="text-left">
                            {availableEncountersForConverstion.length} matched patient encounters were found. If you want to transfer this fee estimate into one of the matched encounter records, please select it.
                        </div>
                        {#each availableEncountersForConverstion as item}
                            <!-- svelte-ignore a11y-click-events-have-key-events -->
                            <!-- svelte-ignore a11y-no-static-element-interactions -->
                            <div class={`flex flex-row w-full pt-2 pb-2 matched-encounter-item ${selectedEncounter?.id == item.id ? 'selected-encounter' : ''}`}>
                                <div on:click={() => onMatchEncounterSelect(item)} class={`flex-1 flex flex-col items-start `}>
                                    <div class="text-lg">
                                        {getFormatDate(item.scheduledDate)}, {getInsuranceType(item.patient.insuranceType, "shortTitle")}
                                    </div>
                                    <div class="text-lg">
                                        {_getSurgeonFullName(item.surgeon) ?? "No surgeon"}, {item.hospital?.name ?? "No Hospital"}
                                    </div>
                                </div>
                                {#if selectedEncounter?.id == item.id}
                                    <div class="flex flex-row items-center justify-center pr-4">
                                        <i class="fa-solid fa-check"></i>
                                    </div>
                                {/if}
                            </div>
                           
                        {/each}
                        <div class="text-left">
                            Note: a quote will need to be sent to the patient to get IFC and/or collect payment.
                        </div>    
                    {:else}
                        <div class="text-left">No future encounters matched for this patient</div>
                    {/if}
                </div>
            {/if}
           
        </div>
    </div>
</NotificationModal>

<ConfirmActionModal
    bind:isConfirmActionModalOpen
    on:confirmAction={() => { 
        isConfirmActionModalOpen = false;
        convertQuoteToEncounter();
    }}
    on:cancelAction={() => isConfirmActionModalOpen = false}
    title={"Confirmation"}
    confirmPrompt={"Are you sure you want to merge this fee estimate into the selected encounter? This operation cannot be undone."}
    confirmButtonText="Confirm"
    cancelButtonText="Cancel"
/>

<style>
    .matched-encounter-item {
        padding-left:15px;
        border-radius: 8px;
    }

    .matched-encounter-item:hover {
        border-left:5px solid grey;
        border-right:5px solid grey;
    }

    .selected-encounter {
        border-left: 5px solid rgb(138, 62, 215);
        border-right: 5px solid rgb(138, 62, 215);
        background: rgb(248 248 248);
    }
</style>
